<template>
  <div class="flex flex-col w-full h-full">
    <!-- Top section with search -->
    <div class="flex mb-4 w-full">
      <div class="field flex-grow-1">
        <label
          for="search"
          class="block text-sm font-medium mb-1"
        >Search Crew Types</label>
        <IconField class="w-full">
          <InputIcon class="pi pi-search" />
          <InputText
            id="search"
            v-model="searchQuery"
            placeholder="Search by role, name, or department..."
            class="w-full"
          />
        </IconField>
      </div>
    </div>

    <div class="flex flex-col md:flex-row w-full gap-3 flex-1 overflow-hidden">
      <!-- LEFT COLUMN: Crew Types List -->
      <div class="w-full md:w-2/3 mb-3 md:mb-0 overflow-hidden flex flex-col">
        <div class="border border-gray-200 rounded-lg shadow-sm h-full flex flex-col">
          <div class="p-3 bg-gray-50 border-b border-gray-200">
            <h3 class="text-lg font-semibold m-0">
              Available Crew Types
            </h3>
          </div>
          <div class="overflow-auto flex-1">
            <DataTable
              :value="filteredItems"
              sort-field="name"
              :row-style="rowStyle"
              :row-class="rowClass"
              striped-rows
              :loading="isLoading"
              class="p-datatable-sm"
              style="min-width: 100%"
              @row-click="onRowClick"
            >
              <template #empty>
                <DataTableEmptyState
                  :is-loading="isLoading"
                  :error="error"
                  loading-message="Loading crew types..."
                  empty-message="No crew types match your criteria."
                  empty-hint="Try adjusting your search."
                  empty-icon="pi pi-users"
                />
              </template>
              <Column
                field="icon"
                header="Icon"
                style="width: 80px"
              >
                <template #body="{ data }">
                  <div class="flex justify-center">
                    <i
                      :class="getRoleIcon(data.name)"
                      class="text-2xl text-blue-500"
                    />
                  </div>
                </template>
              </Column>
              <Column
                field="name"
                header="Role"
                sortable
              >
                <template #body="{ data }">
                  <div>
                    <span class="font-medium">{{ data.name }}</span>
                  </div>
                </template>
              </Column>
              <Column
                field="rate"
                header="Day Rate"
                sortable
                style="width: 120px"
              >
                <template #body="{ data }">
                  <div class="text-right">
                    <input
                      type="number"
                      :value="data.rate"
                      :min="0"
                      :step="0.01"
                      class="w-full px-2 py-1 text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style="width: 100px;"
                      @input="(event) => updateRate(data, event.target.value)"
                    >
                  </div>
                </template>
              </Column>
              <Column
                field="cost"
                header="Cost"
                style="width: 120px"
              >
                <template #body="{ data }">
                  <div class="text-right">
                    <input
                      type="number"
                      :value="data.cost"
                      :min="0"
                      :step="0.01"
                      class="w-full px-2 py-1 text-right border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style="width: 100px;"
                      @input="(event) => updateCost(data, event.target.value)"
                    >
                  </div>
                </template>
              </Column>
              <Column
                header="Qty"
                style="width: 80px"
              >
                <template #body="{ data }">
                  <div class="flex items-center justify-center">
                    <input
                      type="number"
                      :value="data.qtyToAdd || 0"
                      :min="0"
                      :max="10"
                      class="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style="width: 60px;"
                      @input="(event) => selectRow(data, event.target.value)"
                    >
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Selected Crew Panel -->
      <SelectionBasketPanel
        title="Selected Crew"
        :items="selectedCrew"
        :total-count="getSelectedCrewCount()"
        :formatted-total-amount="formatCurrency(calculateTotal())"
        total-count-label="Total Crew"
        total-amount-label="Total Cost"
        :view-mode="selectedViewMode"
        :is-submitting="isSubmitting"
        empty-message="No crew selected"
        empty-hint="Select crew from the table to add them here"
        @clear="clearSelectedCrew"
        @toggle-view="toggleSelectedViewMode"
        @save-and-close="saveCrewAndClose"
        @save-and-keep-open="saveCrewAndKeepOpen"
        @remove="removeSelectedCrew"
      >
        <template #detailed="{ item: crew, remove }">
          <div class="flex align-items-center justify-between mb-3">
            <div class="font-medium">
              {{ crew.name }}
            </div>
            <Button
              icon="pi pi-times"
              text
              rounded
              severity="danger"
              class="p-button-sm"
              @click="remove"
            />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Day Rate</label>
              <InputNumber
                v-model="crew.rate"
                :min="0"
                :max="999999"
                mode="decimal"
                :min-fraction-digits="2"
                class="w-full"
                size="small"
              />
            </div>
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Cost</label>
              <InputNumber
                v-model="crew.cost"
                :min="0"
                :max="999999"
                mode="decimal"
                :min-fraction-digits="2"
                class="w-full"
                size="small"
              />
            </div>
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Qty</label>
              <InputNumber
                v-model="crew.qtyToAdd"
                :min="0"
                :max="999"
                class="w-full"
                size="small"
              />
            </div>
          </div>
          <div class="mt-3 pt-2 border-t border-gray-100">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium">Total:</span>
              <span class="font-bold text-green-600">
                {{ formatCurrency((crew.qtyToAdd || 0) * (crew.rate || 0)) }}
              </span>
            </div>
          </div>
        </template>
      </SelectionBasketPanel>
    </div>

    <!-- Bottom Action -->
    <div class="flex justify-end mt-3">
      <Button
        label="Cancel"
        icon="pi pi-times"
        text
        @click="closeDialog"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineEmits } from 'vue';
import { getCrewTypes } from '@/services/crewTypesService';
import { formatCurrency } from '@/composables/formatCurrency';
import { projectStore } from '../projectStore';
import { useToastService } from '@/composables/useToastService';
import SelectionBasketPanel from '@/components/shared/SelectionBasketPanel.vue';
import DataTableEmptyState from '@/components/shared/DataTableEmptyState.vue';

// Toast service
const toast = useToastService();

// Store the items returned by createCrewTypes.
const items = ref([]);
const selectedCrew = ref([]);

// Initialize items on component mount.
onMounted(async () => {
    try {
        isLoading.value = true;
        error.value = null;
        const response = await getCrewTypes();
        const data = response.data || response;
        
        items.value = data.map(crewType => ({
            ...crewType,
            qtyToAdd: 0 // Initialize quantity for each item.
        }));
        filteredItems.value = items.value;
    } catch (err) {
        console.error('[AddCrewModal] Error fetching crew types:', err);
        error.value = 'Failed to load crew types. Please try again.';
        items.value = [];
        filteredItems.value = [];
    } finally {
        isLoading.value = false;
    }
});

// State variables.
const searchQuery = ref('');
const selectedViewMode = ref('simple'); // 'detailed' | 'simple'
const filteredItems = ref([]);
const isLoading = ref(false);
const error = ref(null);
const isSubmitting = ref(false);

// Watch for search query changes
watch(searchQuery, (newQuery) => {
    if (!newQuery) {
        filteredItems.value = items.value;
    } else {
        const query = newQuery.toLowerCase();
        filteredItems.value = items.value.filter(item => 
            item.name.toLowerCase().includes(query)
        );
    }
});

// Function to handle row clicks in the DataTable.
const onRowClick = (event) => {
    // Set default quantity to 1 if not already set
    if (!event.data.qtyToAdd) {
        event.data.qtyToAdd = 1;
    }
    
    // Select the row
    selectRow(event.data, event.data.qtyToAdd);
};

// Function to select a row and add to selected crew
const selectRow = (crew, qty) => {
    const qtyNum = parseInt(qty) || 0;
    
    if (qtyNum > 0) {
        // Check if crew is already selected
        const existingIndex = selectedCrew.value.findIndex(c => c.id === crew.id);
        
        if (existingIndex >= 0) {
            // Update existing crew quantity
            selectedCrew.value[existingIndex].qtyToAdd = qtyNum;
        } else {
            // Add new crew to selection
            selectedCrew.value.push({
                ...crew,
                qtyToAdd: qtyNum
            });
        }
    } else {
        // Remove crew from selection if quantity is 0
        selectedCrew.value = selectedCrew.value.filter(c => c.id !== crew.id);
    }
    
    // Update the crew item in filteredItems
    crew.qtyToAdd = qtyNum;
};

// Function to remove selected crew
const removeSelectedCrew = (crew) => {
    selectedCrew.value = selectedCrew.value.filter(c => c.id !== crew.id);
    
    // Also reset the qtyToAdd in the filteredItems
    const item = filteredItems.value.find(item => item.id === crew.id);
    if (item) {
        item.qtyToAdd = 0;
    }
};

// Toggle between detailed and simple view for selected crew
const toggleSelectedViewMode = () => {
    selectedViewMode.value = selectedViewMode.value === 'detailed' ? 'simple' : 'detailed';
};

// Perform save to API + refresh + success toast (no close/clear)
const performSave = async () => {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    const parentGroupId = projectStore.currentDraggableItemId;

    if (!projectId || !equipmentListId) {
        throw new Error('Project ID or Equipment List ID not found');
    }

    const { addEquipmentListContent } = await import('@/services/projectService');
    const promises = selectedCrew.value.map(async (crew) => {
        const contentData = {
            type: "item",
            name: crew.name,
            asset_type: "crew",
            quantity: crew.qtyToAdd,
            days: 3,
            price: crew.rate,
            discount: 0,
            notes: "",
            parent_id: parentGroupId ? parseInt(parentGroupId) : null
        };
        return await addEquipmentListContent(projectId, equipmentListId, contentData);
    });

    await Promise.all(promises);

    if (projectStore.refreshFunction && typeof projectStore.refreshFunction === 'function') {
        await projectStore.refreshFunction();
    }

    toast.add({
        severity: 'success',
        summary: 'Crew Added',
        detail: `${getSelectedCrewCount()} crew members added to the project`,
        life: 3000
    });
};

// Save basket and close (same as Add to Project)
const saveCrewAndClose = () => {
    saveCrew();
};

// Save basket and keep window open with items emptied
const saveCrewAndKeepOpen = async () => {
    if (isSubmitting.value || !selectedCrew.value?.length) return;
    isSubmitting.value = true;
    try {
        await performSave();
        emit('crewSubmit', selectedCrew.value);
        clearSelectedCrew();
    } catch (err) {
        console.error('[AddCrewModal] Error adding crew:', err);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.message || err.message || 'Failed to add crew. Please try again.',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Function to clear all selected crew
const clearSelectedCrew = () => {
    // Reset quantities in the table
    filteredItems.value.forEach(item => {
        if (item.qtyToAdd) item.qtyToAdd = 0;
    });

    // Clear the selected crew array
    selectedCrew.value = [];
};

// Get total selected crew count
const getSelectedCrewCount = () => {
    return selectedCrew.value.reduce((total, crew) => total + crew.qtyToAdd, 0);
};

// Calculate total cost
const calculateTotal = () => {
    return selectedCrew.value.reduce((total, crew) => {
        const qty = crew.qtyToAdd || 0;
        const rate = crew.rate || 0;
        return total + (qty * rate);
    }, 0);
};

// Function to save crew to project and close
const saveCrew = async () => {
    if (isSubmitting.value || !selectedCrew.value?.length) return;
    isSubmitting.value = true;
    try {
        await performSave();
        emit('crewSubmit', selectedCrew.value);
        closeDialog();
    } catch (err) {
        console.error('[AddCrewModal] Error adding crew:', err);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.message || err.message || 'Failed to add crew. Please try again.',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Function to close the dialog
const closeDialog = () => {
    // Reset state
    selectedCrew.value = [];
    searchQuery.value = '';
    filteredItems.value = items.value;
    
    // Emit close event
    emit('close');
};

// Function to update rate
const updateRate = (crew, newRate) => {
    const rate = parseFloat(newRate) || 0;
    crew.rate = rate;
    
    // Update the corresponding selected crew if it exists
    const selectedCrewItem = selectedCrew.value.find(c => c.id === crew.id);
    if (selectedCrewItem) {
        selectedCrewItem.rate = rate;
    }
};

// Function to update cost
const updateCost = (crew, newCost) => {
    const cost = parseFloat(newCost) || 0;
    crew.cost = cost;
    
    // Update the corresponding selected crew if it exists
    const selectedCrewItem = selectedCrew.value.find(c => c.id === crew.id);
    if (selectedCrewItem) {
        selectedCrewItem.cost = cost;
    }
};

// Function to get role icon
const getRoleIcon = (roleName) => {
    const role = roleName?.toLowerCase() || '';
    if (role.includes('light')) return 'pi pi-lightbulb';
    if (role.includes('sound')) return 'pi pi-volume-up';
    if (role.includes('video')) return 'pi pi-video';
    if (role.includes('driver')) return 'pi pi-truck';
    if (role.includes('camera')) return 'pi pi-camera';
    if (role.includes('stage')) return 'pi pi-home';
    if (role.includes('rigging')) return 'pi pi-link';
    return 'pi pi-user';
};

// Function to style rows
const rowStyle = (data) => {
    return data.qtyToAdd > 0 ? { backgroundColor: '#E3F2FD' } : {};
};

const rowClass = () => {
    return {
        'cursor-pointer hover:bg-gray-50': true
    };
};

// Define emits
const emit = defineEmits(['crewSubmit', 'close']);
</script>

<style scoped>
/* Style the data table */
:deep(.p-datatable-sm) {
    font-size: 0.875rem;
}

:deep(.p-datatable .p-datatable-tbody > tr) {
    cursor: pointer;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
    background-color: var(--surface-50);
}
</style>
